import {
  DEFAULT_WORKFLOW_CHART_TYPE,
  getIsWorkflowChartDimension,
  getIsWorkflowChartMeasure,
  getIsWorkflowChartType,
  getResolvedWorkflowChartGrain,
  parseWorkflowViewState,
  WORKFLOW_CHART_MEASURE_LABELS,
  WORKFLOW_CHART_MEASURES,
} from '@aline/common';
import type {
  WorkflowChartMeasure,
  WorkflowChartType,
  WorkflowSystemChartPreset,
  WorkflowViewState,
} from '@aline/common';
import { Apollo } from 'src/gql/generated';

export type ChartSeriesDataset = {
  data: (string | number | null)[];
  id: string;
  label: string;
  counts?: number[];
  keys?: string[];
};

export type ChartSeriesResult = {
  dimensions: ChartSeriesDataset[];
  primaryCatalog: InsightChartCategory[];
  secondaryCatalog: InsightChartCategory[];
  totals: {
    counts: number[];
    data: (number | null)[];
  };
  values: ChartSeriesDataset[];
};

export type InsightChartCategory = {
  count: number;
  id: string;
  label: string;
  value: number;
  tooltipLabel?: string;
};

/**
 * Chart-type-agnostic shape for a series result: every Chart type plots the primary
 * Dimension as the category axis (pie slices) and the Measure as the value.
 */
export type InsightChartModel = {
  primaryCatalog: InsightChartCategory[];
  secondaryCatalog: InsightChartCategory[];
  series: {
    data: (number | null)[];
    id: string;
    label: string;
    counts?: number[];
    stack?: string;
  }[];
  totals: {
    counts: number[];
    data: (number | null)[];
  };
  xKeys: string[];
  xLabels: string[];
  yLabel: string;
};

const WORKFLOW_VARIANT_BY_VALUE = {
  Contract: Apollo.WorkflowVariant.Contract,
  Template: Apollo.WorkflowVariant.Template,
} as const satisfies Record<'Contract' | 'Template', Apollo.WorkflowVariant>;

export function getSeriesInputFromSystemPreset(
  preset: WorkflowSystemChartPreset
): Apollo.WorkflowChartSeriesInput {
  return {
    dateBucket: preset.seriesRequest.dateBucket,
    dimensions: [...preset.seriesRequest.dimensions],
    filterModel: preset.seriesRequest.filterModel ?? null,
    grain: preset.seriesRequest.grain,
    measure: preset.seriesRequest.measure,
    minSampleSize: preset.seriesRequest.minSampleSize,
    values: preset.seriesRequest.values ?? null,
    workflowFilter: preset.workflowFilter ?? null,
  };
}

/**
 * Derive a series request from a Pin's Workflow View chart config.
 * Returns null when the chart id or Dimensions/Measure are missing.
 */
export function getSeriesInputFromInsightPin(options: {
  chartId: string;
  viewState: unknown;
}): Apollo.WorkflowChartSeriesInput | null {
  const { chartId, viewState } = options;
  const state = parseWorkflowViewState(viewState);
  const chart = state.chartsIntegration?.charts?.[chartId];
  if (!chart) return null;

  const dimensions = (chart.dimensions ?? [])
    .map(item => (typeof item === 'string' ? item : item.field))
    .filter(getIsWorkflowChartDimension);
  if (!dimensions.length) return null;

  const measureField = (chart.values ?? [])
    .map(item => (typeof item === 'string' ? item : item.field))
    .find(getIsWorkflowChartMeasure);

  return {
    dateBucket: chart.dateBucket,
    dimensions,
    filterModel: state.filter?.filterModel ?? null,
    grain: getResolvedWorkflowChartGrain({
      completed: state.workflowFilter?.completed,
      dimensions,
      filterModel: state.filter?.filterModel,
      grain: chart.grain,
      measure: measureField ?? WORKFLOW_CHART_MEASURES.count,
    }),
    measure: measureField ?? WORKFLOW_CHART_MEASURES.count,
    minSampleSize: chart.minSampleSize,
    values: chart.insightValues ?? null,
    workflowFilter: getWorkflowFilterInput(state.workflowFilter),
  };
}

export function getChartTypeFromInsightPin(options: {
  chartId: string;
  viewState: unknown;
}): WorkflowChartType {
  const { chartId, viewState } = options;
  const chartType =
    parseWorkflowViewState(viewState).chartsIntegration?.charts?.[chartId]?.chartType;
  if (chartType && getIsWorkflowChartType(chartType)) return chartType;
  return DEFAULT_WORKFLOW_CHART_TYPE;
}

function getWorkflowFilterInput(
  filter: WorkflowViewState['workflowFilter']
): Apollo.WorkflowFilterInput | null {
  if (!filter) return null;
  return {
    completed: filter.completed,
    published: filter.published,
    variant: filter.variant != null ? WORKFLOW_VARIANT_BY_VALUE[filter.variant] : undefined,
  };
}

export function getChartModelFromSeries(
  series: ChartSeriesResult | null | undefined,
  measure?: WorkflowChartMeasure,
  overlayValues?: boolean
): InsightChartModel | null {
  if (!series?.dimensions.length || !series.values.length) return null;

  const primaryDimension = series.dimensions.at(0);
  if (!primaryDimension) return null;

  const xLabels = primaryDimension.data.map(value => String(value ?? ''));
  const xKeys =
    primaryDimension.keys?.map(value => String(value)) ??
    primaryDimension.data.map(value => String(value ?? ''));
  const stacked =
    series.values.length > 1 &&
    measure !== WORKFLOW_CHART_MEASURES.medianDurationDays &&
    !overlayValues;
  const measureId = measure ?? (stacked ? WORKFLOW_CHART_MEASURES.count : series.values.at(0)?.id);
  const yLabel =
    measureId === WORKFLOW_CHART_MEASURES.medianDurationDays
      ? WORKFLOW_CHART_MEASURE_LABELS.medianDurationDays
      : WORKFLOW_CHART_MEASURE_LABELS.count;

  return {
    primaryCatalog: series.primaryCatalog,
    secondaryCatalog: series.secondaryCatalog,
    series: series.values.map(valueSeries => ({
      counts: valueSeries.counts,
      data: valueSeries.data.map(value => (typeof value === 'number' ? value : null)),
      id: valueSeries.id,
      label: valueSeries.label,
      ...(stacked ? { stack: 'stack' } : {}),
    })),
    totals: series.totals,
    xKeys,
    xLabels,
    yLabel,
  };
}

export function getIsMedianSuppressed(model: InsightChartModel): boolean {
  if (model.yLabel !== WORKFLOW_CHART_MEASURE_LABELS.medianDurationDays) return false;
  return model.series.every(series => series.data.every(value => value === null));
}

/** Typical time is a day count; extra fraction digits imply a precision we do not have. */
export function formatInsightChartNumber(value: number, wholeDays: boolean): string {
  return value.toLocaleString(undefined, wholeDays ? { maximumFractionDigits: 0 } : undefined);
}

/**
 * A typical time the server withheld for a thin sample arrives as `null`, which draws
 * no bar and leaves the tooltip with nothing to format — indistinguishable from a
 * genuine zero. Formatting the gap names it and reports the sample behind it.
 *
 * Always returns a formatter: MUI merges its own default underneath the series, so an
 * explicit `undefined` here would leave the series with no formatter at all.
 */
export function getInsightValueFormatter(
  model: InsightChartModel,
  series: InsightChartModel['series'][number]
): (value: number | null, context: { dataIndex: number }) => string {
  const suppressible = model.yLabel === WORKFLOW_CHART_MEASURE_LABELS.medianDurationDays;

  return (value, context) => {
    if (value !== null) return formatInsightChartNumber(value, suppressible);
    if (!suppressible) return '';
    const count = series.counts?.at(context.dataIndex) ?? 0;
    if (count === 0) return 'No workflows';
    return `Too few workflows (${count})`;
  };
}
